import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import GoldDivider from '../components/GoldDivider';
import API_URL from '../config.js';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Login Form ───────────────────────────────────────
function LoginForm({ onLogin, t }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      if (!res.ok) {
        throw new Error(t('admin.login_error'));
      }
      const data = await res.json();
      onLogin(data.token);
    } catch (err) {
      setError(err.message || t('admin.login_error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-container" style={{ paddingTop: '2rem' }}>
      <h1 className="page-title">{t('admin.title')}</h1>
      <GoldDivider />
      <div className="card" style={{ maxWidth: '380px', margin: '2rem auto' }}>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: 'var(--gold)',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          {t('admin.login_title')}
        </h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="username">{t('admin.username')}</label>
            <input
              id="username"
              type="text"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">{t('admin.password')}</label>
            <input
              id="password"
              type="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div className="error-box" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{ width: '100%', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? '...' : t('admin.login_btn')}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Codes Tab ────────────────────────────────────────
function CodesTab({ token, t }) {
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [codesData, setCodesData] = useState({ codes: [], stats: { total: 0, used: 0, available: 0 } });
  const [codesLoading, setCodesLoading] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');

  const fetchCodes = useCallback(async () => {
    setCodesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/codes`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCodesData(data);
    } catch {
      // silently ignore on load
    } finally {
      setCodesLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  async function handleGenerate() {
    setGenerateMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/codes/generate`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 200 }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setGenerateMsg(`${data.generated} ${t('admin.codes_generated')}`);
      await fetchCodes();
    } catch {
      setGenerateMsg('Erreur lors de la génération');
    }
  }

  function handleExport() {
    fetch(`${API_URL}/api/admin/codes/export`, { headers: authHeaders })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'invite_codes.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Export failed'));
  }

  async function handleDeleteUnused() {
    if (!window.confirm(t('admin.codes_delete_confirm'))) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/codes/unused`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed');
      await fetchCodes();
    } catch {
      alert('Erreur lors de la suppression');
    }
  }

  const { codes, stats } = codesData;

  return (
    <div>
      {/* Stats row */}
      <div className="stats-row" style={{ marginBottom: '1.5rem' }}>
        {[
          { label: t('admin.codes_total'), value: stats.total },
          { label: t('admin.codes_used'), value: stats.used },
          { label: t('admin.codes_available'), value: stats.available },
        ].map((stat) => (
          <div key={stat.label} className="stat-box">
            <div className="stat-num">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <button onClick={handleGenerate} className="btn-primary btn-small">
          {t('admin.codes_generate')}
        </button>
        <button onClick={handleExport} className="btn-secondary btn-small">
          {t('admin.codes_export')}
        </button>
        <button onClick={handleDeleteUnused} className="btn-danger btn-small">
          {t('admin.codes_delete_unused')}
        </button>
        {generateMsg && (
          <span
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.72rem',
              color: 'var(--gold)',
              letterSpacing: '0.08em',
            }}
          >
            ✦ {generateMsg}
          </span>
        )}
      </div>

      {/* Codes table */}
      {codesLoading ? (
        <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)' }}>
          Chargement...
        </p>
      ) : codes.length === 0 ? (
        <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)', padding: '2rem' }}>
          Aucun code généré.
        </p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t('admin.code_col')}</th>
                <th>{t('admin.code_status_col')}</th>
                <th>{t('admin.code_used_at_col')}</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span
                      style={{
                        fontFamily: "'Cinzel', serif",
                        fontSize: '0.9rem',
                        letterSpacing: '0.2em',
                        color: 'var(--cream)',
                      }}
                    >
                      {c.code}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={c.used ? 'badge badge-nein' : 'badge badge-sekt'}>
                      {c.used ? t('admin.code_used') : t('admin.code_available')}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {c.used_at ? formatDate(c.used_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────
function Dashboard({ token, onLogout, t }) {
  const [activeTab, setActiveTab] = useState('guests');
  const [guestsData, setGuestsData] = useState({ guests: [], stats: {} });
  const [guestbook, setGuestbook] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchGuests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/guests`, { headers: authHeaders });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setGuestsData(data);
    } catch {
      onLogout();
    }
  }, [token]); // eslint-disable-line

  const fetchGuestbook = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/guestbook`, { headers: authHeaders });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setGuestbook(data);
    } catch {
      onLogout();
    }
  }, [token]); // eslint-disable-line

  useEffect(() => {
    async function load() {
      setLoading(true);
      await Promise.all([fetchGuests(), fetchGuestbook()]);
      setLoading(false);
    }
    load();
  }, [fetchGuests, fetchGuestbook]);

  async function handleToggleApproval(id, currentApproved) {
    try {
      const res = await fetch(`${API_URL}/api/admin/guestbook/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: !currentApproved }),
      });
      if (!res.ok) throw new Error('Failed');
      const updated = await res.json();
      setGuestbook((prev) => prev.map((e) => (e.id === id ? updated : e)));
    } catch {
      alert('Error updating entry');
    }
  }

  function handleExportCSV() {
    fetch(`${API_URL}/api/admin/export`, { headers: authHeaders })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'guests.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Export failed'));
  }

  const { stats, guests } = guestsData;

  const tabStyle = (tab) => ({
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--gold)' : '2px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontFamily: "'Cinzel', serif",
    fontSize: '0.75rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: activeTab === tab ? 'var(--gold)' : 'rgba(245,233,198,0.5)',
    transition: 'all 0.2s',
  });

  return (
    <div className="page-container" style={{ paddingTop: '1rem' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: 'var(--cream)',
            fontSize: '1.8rem',
            margin: 0,
          }}
        >
          {t('admin.title')}
        </h1>
        <button onClick={onLogout} className="btn-secondary btn-small">
          {t('admin.logout')}
        </button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)' }}>{t('common.loading')}</p>
      ) : (
        <>
          {/* Stats bar */}
          <div className="stats-row">
            {[
              { label: t('admin.total_guests'), value: stats.total_guests || 0 },
              { label: t('admin.attending_count'), value: stats.attending_count || 0 },
              { label: t('admin.not_attending_count'), value: stats.not_attending_count || 0 },
            ].map((stat) => (
              <div key={stat.label} className="stat-box">
                <div className="stat-num">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="admin-tabs">
            <button style={tabStyle('guests')} onClick={() => setActiveTab('guests')}>
              {t('admin.guests_tab')} ({guests.length})
            </button>
            <button style={tabStyle('guestbook')} onClick={() => setActiveTab('guestbook')}>
              {t('admin.guestbook_tab')} ({guestbook.length})
            </button>
            <button style={tabStyle('codes')} onClick={() => setActiveTab('codes')}>
              {t('admin.codes_tab')}
            </button>
          </div>

          {/* Guests tab */}
          {activeTab === 'guests' && (
            <div>
              <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                <button onClick={handleExportCSV} className="btn-primary btn-small">
                  {t('admin.export_csv')}
                </button>
              </div>
              {guests.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)', padding: '2rem' }}>
                  Aucun invité enregistré.
                </p>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('admin.name_col')}</th>
                        <th>{t('admin.email_col')}</th>
                        <th>{t('admin.plus_ones_col')}</th>
                        <th>{t('admin.attending_col')}</th>
                        <th>{t('admin.dietary_col')}</th>
                        <th>{t('admin.date_col')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guests.map((g) => (
                        <tr key={g.id}>
                          <td>
                            <strong>
                              {g.first_name} {g.last_name}
                            </strong>
                          </td>
                          <td>{g.email || '—'}</td>
                          <td style={{ textAlign: 'center' }}>{g.plus_ones}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={g.attending ? 'badge badge-gala' : 'badge badge-nein'}>
                              {g.attending ? t('admin.yes') : t('admin.no')}
                            </span>
                          </td>
                          <td style={{ maxWidth: '180px', fontSize: '0.85rem' }}>
                            {g.dietary_needs || '—'}
                          </td>
                          <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {formatDate(g.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Guestbook tab */}
          {activeTab === 'guestbook' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {guestbook.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)', padding: '2rem' }}>
                  Aucun message.
                </p>
              ) : (
                guestbook.map((entry) => (
                  <div
                    key={entry.id}
                    className="card"
                    style={{
                      borderLeft: `4px solid ${entry.approved ? '#4caf50' : '#ffa726'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'center',
                          marginBottom: '0.4rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <strong
                          style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            color: 'var(--gold)',
                            fontSize: '1.05rem',
                          }}
                        >
                          {entry.name}
                        </strong>
                        <span style={{ fontSize: '0.72rem', color: 'rgba(245,233,198,0.5)' }}>
                          {formatDate(entry.created_at)}
                        </span>
                        <span className={entry.approved ? 'badge badge-gala' : 'badge badge-pending'}>
                          {entry.approved ? t('admin.status_approved') : t('admin.status_pending')}
                        </span>
                      </div>
                      <p style={{ color: 'var(--cream)', margin: 0, lineHeight: 1.6 }}>
                        {entry.message}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleApproval(entry.id, entry.approved)}
                      className={entry.approved ? 'btn-danger btn-small' : 'btn-success btn-small'}
                      style={{ flexShrink: 0 }}
                    >
                      {entry.approved ? t('admin.hide') : t('admin.approve')}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Codes tab */}
          {activeTab === 'codes' && <CodesTab token={token} t={t} />}
        </>
      )}
    </div>
  );
}

// ─── Admin (main component) ───────────────────────────
export default function Admin() {
  const { t } = useLanguage();
  const [token, setToken] = useState(() => localStorage.getItem('michel50_admin_token'));

  function handleLogin(newToken) {
    localStorage.setItem('michel50_admin_token', newToken);
    setToken(newToken);
  }

  function handleLogout() {
    localStorage.removeItem('michel50_admin_token');
    setToken(null);
  }

  if (!token) {
    return <LoginForm onLogin={handleLogin} t={t} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} t={t} />;
}
